///
/// EarthBrowser
///
/// Created by Matt Giger on December 15, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import type { GeoPackageCatalog } from "@shared-types/geopackage-catalog"

/**
 * Raw row shape returned by queries against the geopackage_catalog table.
 * JSON columns (tags, mirror_urls, metadata) and booleans are stored as
 * text/integers and converted in rowToPackage.
 */
interface GeoPackageCatalogRow {
	id: string
	package_id: string
	name: string
	description: string | null
	version: string
	category: GeoPackageCatalog['category']
	subcategory: string | null
	tags: string | null
	filename: string
	file_size: number
	file_hash: string | null
	bbox_min_x: number | null
	bbox_min_y: number | null
	bbox_max_x: number | null
	bbox_max_y: number | null
	coordinate_system: string
	min_zoom: number | null
	max_zoom: number | null
	feature_count: number | null
	cdn_url: string
	mirror_urls: string | null
	source: string | null
	attribution: string | null
	attribution_url: string | null
	license: string | null
	license_url: string | null
	visibility: GeoPackageCatalog['visibility']
	requires_subscription: number
	subscription_tier: GeoPackageCatalog['subscription_tier']
	created_by: string | null
	organization: string | null
	status: GeoPackageCatalog['status']
	quality_score: number | null
	temporary: number
	download_count: number
	install_count: number
	rating_avg: number | null
	rating_count: number
	created_at: string
	updated_at: string
	last_downloaded_at: string | null
	metadata: string | null
}

export class GeoPackageCatalogTable {

	public static initialize(db: Database) {
		db.enqueue([
			{ sql: `create table if not exists geopackage_catalog (
				id text primary key,
				package_id text unique not null,
				name text not null,
				description text,
				version text not null,
				category text not null,
				subcategory text,
				tags text,
				filename text not null,
				file_size integer not null,
				file_hash text,
				bbox_min_x real,
				bbox_min_y real,
				bbox_max_x real,
				bbox_max_y real,
				coordinate_system text,
				min_zoom integer,
				max_zoom integer,
				feature_count integer,
				cdn_url text not null,
				mirror_urls text,
				source text,
				attribution text,
				attribution_url text,
				license text,
				license_url text,
				visibility text not null,
				requires_subscription integer not null default 0,
				subscription_tier text,
				created_by text,
				organization text,
				status text not null,
				quality_score real,
				temporary integer not null default 0,
				download_count integer not null default 0,
				install_count integer not null default 0,
				rating_avg real,
				rating_count integer not null default 0,
				created_at text not null,
				updated_at text not null,
				last_downloaded_at text,
				installed_at text not null,
				metadata text
			)` },
			{ sql: 'create index if not exists geopackage_catalog_package_id on geopackage_catalog (package_id)' },
			{ sql: 'create index if not exists geopackage_catalog_category on geopackage_catalog (category)' },
			{ sql: 'create index if not exists geopackage_catalog_name on geopackage_catalog (name)' },
		], "critical")
	}

	public static async insert(pkg: GeoPackageCatalog): Promise<void> {
		if (Database.db.failed) {
			throw new Error('Database is not available')
		}

		const statement = `INSERT INTO geopackage_catalog (
			id, package_id, name, description, version, category, subcategory, tags,
			filename, file_size, file_hash, bbox_min_x, bbox_min_y, bbox_max_x, bbox_max_y,
			coordinate_system, min_zoom, max_zoom, feature_count, cdn_url, mirror_urls,
			source, attribution, attribution_url, license, license_url, visibility,
			requires_subscription, subscription_tier, created_by, organization, status,
			quality_score, temporary, download_count, install_count, rating_avg, rating_count,
			created_at, updated_at, last_downloaded_at, installed_at, metadata
		) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
		ON CONFLICT(id) DO UPDATE SET
			name = excluded.name,
			description = excluded.description,
			version = excluded.version,
			category = excluded.category,
			subcategory = excluded.subcategory,
			tags = excluded.tags,
			filename = excluded.filename,
			file_size = excluded.file_size,
			file_hash = excluded.file_hash,
			bbox_min_x = excluded.bbox_min_x,
			bbox_min_y = excluded.bbox_min_y,
			bbox_max_x = excluded.bbox_max_x,
			bbox_max_y = excluded.bbox_max_y,
			coordinate_system = excluded.coordinate_system,
			min_zoom = excluded.min_zoom,
			max_zoom = excluded.max_zoom,
			feature_count = excluded.feature_count,
			cdn_url = excluded.cdn_url,
			mirror_urls = excluded.mirror_urls,
			source = excluded.source,
			attribution = excluded.attribution,
			attribution_url = excluded.attribution_url,
			license = excluded.license,
			license_url = excluded.license_url,
			visibility = excluded.visibility,
			requires_subscription = excluded.requires_subscription,
			subscription_tier = excluded.subscription_tier,
			organization = excluded.organization,
			status = excluded.status,
			quality_score = excluded.quality_score,
			temporary = excluded.temporary,
			updated_at = excluded.updated_at,
			installed_at = excluded.installed_at`

		await Database.db.exec(statement, [
			pkg.id,
			pkg.package_id,
			pkg.name,
			pkg.description,
			pkg.version,
			pkg.category,
			pkg.subcategory,
			Array.isArray(pkg.tags) ? JSON.stringify(pkg.tags) : pkg.tags,
			pkg.filename,
			pkg.file_size,
			pkg.file_hash,
			pkg.bbox_min_x,
			pkg.bbox_min_y,
			pkg.bbox_max_x,
			pkg.bbox_max_y,
			pkg.coordinate_system,
			pkg.min_zoom,
			pkg.max_zoom,
			pkg.feature_count,
			pkg.cdn_url,
			Array.isArray(pkg.mirror_urls) ? JSON.stringify(pkg.mirror_urls) : pkg.mirror_urls,
			pkg.source,
			pkg.attribution,
			pkg.attribution_url,
			pkg.license,
			pkg.license_url,
			pkg.visibility,
			pkg.requires_subscription ? 1 : 0,
			pkg.subscription_tier,
			pkg.created_by,
			pkg.organization,
			pkg.status,
			pkg.quality_score,
			pkg.temporary ? 1 : 0,
			pkg.download_count,
			pkg.install_count,
			pkg.rating_avg,
			pkg.rating_count,
			pkg.created_at,
			pkg.updated_at,
			pkg.last_downloaded_at,
			new Date().toISOString(),
			typeof pkg.metadata === 'object' ? JSON.stringify(pkg.metadata) : pkg.metadata,
		])
	}

	public static async get(id: string): Promise<GeoPackageCatalog | null> {
		if (Database.db.failed) {
			return null
		}

		try {
			const statement = 'select * from geopackage_catalog where id = ?'
			const result = await Database.db.exec(statement, [id]) as GeoPackageCatalogRow[]

			if (result.length === 0) return null

			const row = result[0]
			return this.rowToPackage(row)
		} catch (error) {
			console.error('Failed to get package from geopackage_catalog:', error)
			return null
		}
	}

	public static async getByPackageId(packageId: string): Promise<GeoPackageCatalog | null> {
		if (Database.db.failed) {
			return null
		}

		try {
			const statement = 'select * from geopackage_catalog where package_id = ?'
			const result = await Database.db.exec(statement, [packageId]) as GeoPackageCatalogRow[]

			if (result.length === 0) return null

			const row = result[0]
			return this.rowToPackage(row)
		} catch (error) {
			console.error('Failed to get package by package_id from geopackage_catalog:', error)
			return null
		}
	}

	public static async list(): Promise<GeoPackageCatalog[]> {
		if (Database.db.failed) {
			return []
		}

		try {
			const statement = 'select * from geopackage_catalog order by installed_at desc'
			const result = await Database.db.exec(statement) as GeoPackageCatalogRow[]
			return result.map(row => this.rowToPackage(row))
		} catch (error) {
			// Table might not exist yet or query failed
			console.error('Failed to list geopackage_catalog:', error)
			// Return empty array instead of throwing
			return []
		}
	}

	public static async delete(id: string): Promise<void> {
		if (Database.db.failed) {
			throw new Error('Database is not available')
		}

		const statement = 'delete from geopackage_catalog where id = ?'
		await Database.db.exec(statement, [id])
	}

	public static async drop(): Promise<void> {
		await Database.db.exec('drop table if exists geopackage_catalog')
	}

	private static safeJsonParse<T>(value: unknown, fallback: T): T {
		if (!value) return fallback
		if (typeof value !== 'string') return value as T
		if (value.trim() === '') return fallback

		try {
			return JSON.parse(value) as T
		} catch (error) {
			// Try to parse PostgreSQL array format: {item1,item2,item3}
			if (value.startsWith('{') && value.endsWith('}')) {
				try {
					const content = value.slice(1, -1)
					if (content.trim() === '') return fallback
					const items = content.split(',').map(item => item.trim())
					return items as unknown as T
				} catch (pgError) {
					console.warn('Failed to parse PostgreSQL array format:', value, pgError)
				}
			}
			console.warn('Failed to parse JSON:', value, error)
			return fallback
		}
	}

	private static rowToPackage(row: GeoPackageCatalogRow): GeoPackageCatalog {
		return {
			id: row.id,
			package_id: row.package_id,
			name: row.name,
			description: row.description,
			version: row.version,
			category: row.category,
			subcategory: row.subcategory,
			tags: this.safeJsonParse<string[]>(row.tags, []),
			filename: row.filename,
			file_size: row.file_size,
			file_hash: row.file_hash,
			bbox_min_x: row.bbox_min_x,
			bbox_min_y: row.bbox_min_y,
			bbox_max_x: row.bbox_max_x,
			bbox_max_y: row.bbox_max_y,
			coordinate_system: row.coordinate_system,
			min_zoom: row.min_zoom,
			max_zoom: row.max_zoom,
			feature_count: row.feature_count,
			cdn_url: row.cdn_url,
			mirror_urls: this.safeJsonParse<string[]>(row.mirror_urls, []),
			source: row.source,
			attribution: row.attribution,
			attribution_url: row.attribution_url,
			license: row.license,
			license_url: row.license_url,
			visibility: row.visibility,
			requires_subscription: Boolean(row.requires_subscription),
			subscription_tier: row.subscription_tier,
			created_by: row.created_by,
			organization: row.organization,
			status: row.status,
			quality_score: row.quality_score,
			temporary: Boolean(row.temporary),
			download_count: row.download_count,
			install_count: row.install_count,
			rating_avg: row.rating_avg,
			rating_count: row.rating_count,
			created_at: row.created_at,
			updated_at: row.updated_at,
			last_downloaded_at: row.last_downloaded_at,
			metadata: this.safeJsonParse<Record<string, unknown>>(row.metadata, {}),
		}
	}
}

